// Sweet Swap - pure match-3 logic: deterministic board generation, run
// detection, striped specials, cascades and gravity. No DOM imports.
//
// The board is a full W x H grid of candies. A move swaps two adjacent candies
// and is only legal if it creates a run of MATCH_MIN or more of one color.
// Runs clear (a run of exactly 4 leaves one striped candy behind that wipes
// its whole row/column when it is later cleared; a run of 5+ leaves a color
// bomb that wipes every candy of one color), candies fall, new ones drop in,
// and any new runs cascade with a rising chain multiplier. Swapping a color
// bomb with any candy is always a legal move: the bomb detonates and clears
// all candies of the partner's color (two bombs together clear the board).
//
// Levels are endless and generated deterministically from their number: you
// get a moves budget and a target score. Refills come from a seeded RNG so a
// restart replays the exact same board and drop sequence.

export const W = 8; // columns
export const H = 8; // rows
export const MATCH_MIN = 3; // run length at which candies clear

/** 0 = plain, 1 = striped row-clearer, 2 = striped column-clearer, 3 = color bomb. */
export type Special = 0 | 1 | 2 | 3;

export interface Cell {
  color: number; // 0-based color index
  special: Special;
}

/** board[r][c]; null only transiently while a clear step is being resolved. */
export type Board = (Cell | null)[][];

export interface SwapLevel {
  n: number;
  colors: number; // distinct colors in play (indices 0..colors-1)
  moves: number; // swap budget
  target: number; // score needed to win
  seed: number; // RNG seed for the board fill + refills
}

// ---- Deterministic RNG (mulberry32, same scheme as the other games) --------

export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- Level shape ------------------------------------------------------------

/** Difficulty curve: more colors as n grows; the target/move ratio rises too. */
export function shapeForSwapLevel(n: number): { colors: number; moves: number; target: number } {
  const colors = Math.min(6, 4 + Math.floor((n - 1) / 8));
  const moves = 20;
  // Cascades average well over 60 points per move, so the target has to sit
  // far above moves * 30 or a handful of lucky swaps ends the level.
  const target = moves * (60 + Math.min(60, 3 * n));
  return { colors, moves, target };
}

export function generateSwapLevel(n: number): SwapLevel {
  const { colors, moves, target } = shapeForSwapLevel(n);
  return { n, colors, moves, target, seed: (12000 + n * 2654435761) >>> 0 };
}

// ---- Board helpers ----------------------------------------------------------

export function inBoard(r: number, c: number): boolean {
  return r >= 0 && r < H && c >= 0 && c < W;
}

export function adjacent(a: [number, number], b: [number, number]): boolean {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) === 1;
}

export function swapCells(board: Board, a: [number, number], b: [number, number]): void {
  const t = board[a[0]][a[1]];
  board[a[0]][a[1]] = board[b[0]][b[1]];
  board[b[0]][b[1]] = t;
}

/** Is (r,c) part of a run of MATCH_MIN+ of its own color? */
export function matchAt(board: Board, r: number, c: number): boolean {
  const cell = board[r][c];
  if (!cell) return false;
  let h = 1;
  for (let cc = c - 1; cc >= 0 && board[r][cc]?.color === cell.color; cc--) h++;
  for (let cc = c + 1; cc < W && board[r][cc]?.color === cell.color; cc++) h++;
  if (h >= MATCH_MIN) return true;
  let v = 1;
  for (let rr = r - 1; rr >= 0 && board[rr][c]?.color === cell.color; rr--) v++;
  for (let rr = r + 1; rr < H && board[rr][c]?.color === cell.color; rr++) v++;
  return v >= MATCH_MIN;
}

/** Would swapping a and b create a match? Leaves the board untouched. */
export function swapMakesMatch(board: Board, a: [number, number], b: [number, number]): boolean {
  if (!board[a[0]][a[1]] || !board[b[0]][b[1]]) return false;
  if (board[a[0]][a[1]]!.color === board[b[0]][b[1]]!.color) return false;
  swapCells(board, a, b);
  const ok = matchAt(board, a[0], a[1]) || matchAt(board, b[0], b[1]);
  swapCells(board, a, b);
  return ok;
}

/** Any legal move on the board (used for hints and the shuffle check). */
export function findMove(board: Board): [[number, number], [number, number]] | null {
  for (let r = 0; r < H; r++) {
    for (let c = 0; c < W; c++) {
      if (c + 1 < W && swapMakesMatch(board, [r, c], [r, c + 1])) return [[r, c], [r, c + 1]];
      if (r + 1 < H && swapMakesMatch(board, [r, c], [r + 1, c])) return [[r, c], [r + 1, c]];
    }
  }
  return null;
}

// ---- Run detection ----------------------------------------------------------

export interface Run {
  horiz: boolean;
  color: number;
  cells: Array<[number, number]>;
}

export function findRuns(board: Board): Run[] {
  const runs: Run[] = [];
  const scan = (line: Array<[number, number]>, horiz: boolean): void => {
    let i = 0;
    while (i < line.length) {
      const cell = board[line[i][0]][line[i][1]];
      if (!cell) {
        i++;
        continue;
      }
      let j = i + 1;
      while (j < line.length && board[line[j][0]][line[j][1]]?.color === cell.color) j++;
      if (j - i >= MATCH_MIN) runs.push({ horiz, color: cell.color, cells: line.slice(i, j) });
      i = j;
    }
  };
  for (let r = 0; r < H; r++) {
    scan(Array.from({ length: W }, (_, c) => [r, c] as [number, number]), true);
  }
  for (let c = 0; c < W; c++) {
    scan(Array.from({ length: H }, (_, r) => [r, c] as [number, number]), false);
  }
  return runs;
}

// ---- Clearing ---------------------------------------------------------------

export interface ClearResult {
  /** Cells removed this step (their board slots are now null). */
  cleared: Array<[number, number]>;
  /** Striped candies left behind by runs of 4+ (already placed on the board). */
  spawned: Array<{ r: number; c: number; cell: Cell }>;
  /** Specials that detonated this step (striped row/column wipes, color bombs), for view effects. */
  fired: Array<{ r: number; c: number; special: Special }>;
  points: number;
}

/**
 * Clear all current runs once. Runs of exactly 4 leave a striped candy behind
 * (horizontal run -> column-clearer, vertical run -> row-clearer); runs of 5+
 * leave a color bomb. A striped candy caught in a clear wipes its entire
 * row/column and a color bomb caught in a clear wipes every candy of its own
 * color, recursively. Returns null when there is nothing to clear. `focus`
 * (the cell the player swapped to) gets the special spawn when it is part of
 * the run. `forced` cells (from a bomb swap) clear even without a run.
 */
export function clearStep(
  board: Board,
  chain: number,
  focus?: [number, number] | null,
  forced?: Array<[number, number]>,
): ClearResult | null {
  const runs = findRuns(board);
  if (runs.length === 0 && !forced?.length) return null;

  const key = (r: number, c: number): string => `${r},${c}`;
  const spawnAt = new Map<string, Cell>();
  for (const run of runs) {
    if (run.cells.length < 4) continue;
    let pos = run.cells[Math.floor(run.cells.length / 2)];
    if (focus) {
      const hit = run.cells.find(([r, c]) => r === focus[0] && c === focus[1]);
      if (hit) pos = hit;
    }
    const special: Special = run.cells.length >= 5 ? 3 : run.horiz ? 2 : 1;
    spawnAt.set(key(pos[0], pos[1]), { color: run.color, special });
  }

  const cleared = new Set<string>();
  const fired: ClearResult["fired"] = [];
  const queue: Array<[number, number]> = [];
  const push = (r: number, c: number): void => {
    const k = key(r, c);
    if (spawnAt.has(k) || cleared.has(k) || !board[r][c]) return;
    cleared.add(k);
    queue.push([r, c]);
  };
  for (const run of runs) for (const [r, c] of run.cells) push(r, c);
  if (forced) for (const [r, c] of forced) push(r, c);
  while (queue.length) {
    const [r, c] = queue.pop()!;
    const cell = board[r][c]!;
    if (cell.special !== 0) fired.push({ r, c, special: cell.special });
    if (cell.special === 1) for (let cc = 0; cc < W; cc++) push(r, cc);
    if (cell.special === 2) for (let rr = 0; rr < H; rr++) push(rr, c);
    if (cell.special === 3) {
      for (let rr = 0; rr < H; rr++) {
        for (let cc = 0; cc < W; cc++) {
          if (board[rr][cc]?.color === cell.color) push(rr, cc);
        }
      }
    }
  }

  const clearedCells: Array<[number, number]> = [];
  for (const k of cleared) {
    const [r, c] = k.split(",").map(Number);
    board[r][c] = null;
    clearedCells.push([r, c]);
  }
  const spawned: ClearResult["spawned"] = [];
  for (const [k, cell] of spawnAt) {
    const [r, c] = k.split(",").map(Number);
    board[r][c] = cell;
    spawned.push({ r, c, cell });
  }

  return { cleared: clearedCells, spawned, fired, points: cleared.size * 10 * chain };
}

// ---- Gravity + refill ---------------------------------------------------------

export interface GravityResult {
  /** Every surviving cell's column move (fromR === toR when it didn't move). */
  moves: Array<{ c: number; fromR: number; toR: number }>;
  /** Fresh candies dropped in from above, already placed on the board. */
  spawns: Array<{ c: number; toR: number; cell: Cell }>;
}

export function applyGravity(board: Board, rng: () => number, colors: number): GravityResult {
  const moves: GravityResult["moves"] = [];
  const spawns: GravityResult["spawns"] = [];
  for (let c = 0; c < W; c++) {
    let write = H - 1;
    for (let r = H - 1; r >= 0; r--) {
      const cell = board[r][c];
      if (!cell) continue;
      if (write !== r) {
        board[write][c] = cell;
        board[r][c] = null;
      }
      moves.push({ c, fromR: r, toR: write });
      write--;
    }
    for (let r = write; r >= 0; r--) {
      const cell: Cell = { color: Math.floor(rng() * colors), special: 0 };
      board[r][c] = cell;
      spawns.push({ c, toR: r, cell });
    }
  }
  return { moves, spawns };
}

// ---- Generation & shuffling ---------------------------------------------------

/** Fill a fresh board with no pre-made runs and at least one legal move. */
export function fillBoard(rng: () => number, colors: number): Board {
  for (let attempt = 0; attempt < 100; attempt++) {
    const board: Board = [];
    for (let r = 0; r < H; r++) {
      const row: (Cell | null)[] = [];
      for (let c = 0; c < W; c++) {
        let color: number;
        do {
          color = Math.floor(rng() * colors);
        } while (
          (c >= 2 && row[c - 1]?.color === color && row[c - 2]?.color === color) ||
          (r >= 2 && board[r - 1][c]?.color === color && board[r - 2][c]?.color === color)
        );
        row.push({ color, special: 0 });
      }
      board.push(row);
    }
    if (findMove(board)) return board;
  }
  // With 4+ colors on 8x8 a move-less fill is astronomically rare; the loop
  // above retrying 100 times makes this unreachable in practice.
  throw new Error("fillBoard: could not produce a movable board");
}

/**
 * Reshuffle the existing candies in place (used when no legal move remains).
 * Keeps the exact multiset of candies, avoids creating instant runs, and
 * guarantees a legal move afterwards.
 */
export function shuffleBoard(board: Board, rng: () => number, colors: number): void {
  const cells: Cell[] = [];
  for (const row of board) for (const cell of row) if (cell) cells.push(cell);
  for (let attempt = 0; attempt < 60; attempt++) {
    // Constructive placement: shuffle the pool, then lay candies down row-major,
    // at each slot taking the first pooled candy that doesn't complete a run.
    // (A blind permutation almost always contains a run on a full 8x8 board.)
    const pool = cells.slice();
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    let stuck = false;
    for (let r = 0; r < H && !stuck; r++) {
      for (let c = 0; c < W && !stuck; c++) {
        const idx = pool.findIndex(
          (cell) =>
            !(c >= 2 && board[r][c - 1]?.color === cell.color && board[r][c - 2]?.color === cell.color) &&
            !(r >= 2 && board[r - 1][c]?.color === cell.color && board[r - 2][c]?.color === cell.color),
        );
        if (idx < 0) {
          stuck = true;
          break;
        }
        board[r][c] = pool[idx];
        pool.splice(idx, 1);
      }
    }
    if (!stuck && findMove(board)) return;
  }
  // Pathological candy mix (e.g. nearly all one color): rebuild fresh instead.
  const fresh = fillBoard(rng, colors);
  for (let r = 0; r < H; r++) for (let c = 0; c < W; c++) board[r][c] = fresh[r][c];
}
